import React from "react";
import { X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "components/common/Modal";

/**
 * Shared modal shell for manufacturing forms: gradient header with icon/title,
 * scrollable body, and a footer with cancel / submit + inline error.
 */
const ModalShell = ({
  show,
  onClose,
  icon: Icon,
  title,
  subtitle,
  children,
  onSubmit,
  submitLabel,
  submitting = false,
  submitDisabled = false,
  error,
  destructive = false,
  maxWidth = "520px",
}) => {
  const { t } = useTranslation();
  const canSubmit = !submitDisabled && !submitting;

  return (
    <Modal
      show={show}
      onClose={submitting ? () => {} : onClose}
      maxWidth={maxWidth}
    >
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: destructive
            ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
            : "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div className="relative flex items-start justify-between">
          <div>
            {Icon && (
              <div
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: "rgba(255, 255, 255, 0.2)" }}
              >
                <Icon size={24} className="text-white" />
              </div>
            )}
            <h2 className="text-xl font-bold text-white">{title}</h2>
            {subtitle && (
              <p
                className="mt-1.5 text-sm"
                style={{ color: "rgba(255, 255, 255, 0.85)" }}
              >
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150 disabled:opacity-60"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="max-h-[60vh] overflow-y-auto p-7">{children}</div>

      {/* Error */}
      {error && (
        <div className="mx-7 -mt-3 mb-1 whitespace-pre-line rounded-lg bg-[#FEF2F2] px-3 py-2.5 text-xs text-[#DC2626]">
          {error}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={onClose}
          disabled={submitting}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          className="flex items-center justify-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            canSubmit
              ? {
                  background: destructive
                    ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
                    : "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  boxShadow: destructive
                    ? "0 2px 8px rgba(220, 38, 38, 0.3)"
                    : "0 2px 8px rgba(224, 122, 58, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitLabel ?? t("common.actions.save")}
        </button>
      </div>
    </Modal>
  );
};

export default ModalShell;
